import type { StatementImportConfirmInputV2, StatementImportDiscardInputV2,
  StatementImportIntentInputV2, StatementImportReconciliationInputV2,
  StatementImportReviewDecisionV2, StatementImportReviewInputV2 } from
  "../../contracts/src/portfolio-v2/index.js";
import { stableFingerprint } from "./decimal.js";

const SCHEMA_VERSION = "tradvera.portfolio-v2.v1";

export function statementImportReviewInputFingerprintV2(input: StatementImportReviewInputV2): string {
  schema(input.schemaVersion);
  const decisions = input.decisions.map(decision).sort((left, right) => left.fieldId.localeCompare(right.fieldId));
  if (!decisions.length || new Set(decisions.map((item) => item.fieldId)).size !== decisions.length) invalid();
  return fingerprint("REVIEW", { importId: id(input.importId), expectedRevision: uint(input.expectedRevision),
    idempotencyKey: id(input.idempotencyKey), decisions });
}

export function statementImportReconciliationInputFingerprintV2(input: StatementImportReconciliationInputV2): string {
  schema(input.schemaVersion);
  return fingerprint("RECONCILIATION", { importId: id(input.importId), expectedRevision: uint(input.expectedRevision),
    idempotencyKey: id(input.idempotencyKey) });
}

export function statementImportIntentInputFingerprintV2(input: StatementImportIntentInputV2): string {
  schema(input.schemaVersion);
  if (input.ownerReviewAcknowledged !== true || input.duplicateReviewAcknowledged !== true) invalid();
  return fingerprint("ACCEPT_INTENT", { importId: id(input.importId),
    expectedBookVersion: uint(input.expectedBookVersion),
    reconciliationFingerprint: stable(input.reconciliationFingerprint),
    idempotencyKey: id(input.idempotencyKey), ownerReviewAcknowledged: true,
    duplicateReviewAcknowledged: true });
}

export function statementImportConfirmInputFingerprintV2(input: StatementImportConfirmInputV2): string {
  schema(input.schemaVersion);
  if (input.ownerConfirmation !== true || input.observationsOnlyAcknowledged !== true) invalid();
  return fingerprint("ACCEPT_CONFIRM", { importId: id(input.importId), intentId: id(input.intentId),
    exactBookId: id(input.exactBookId), challengeToken: text(input.challengeToken, 8, 160),
    intentFingerprint: stable(input.intentFingerprint), idempotencyKey: id(input.idempotencyKey),
    ownerConfirmation: true, observationsOnlyAcknowledged: true });
}

export function statementImportDiscardInputFingerprintV2(
  input: Omit<StatementImportDiscardInputV2, "inputFingerprint">,
): string {
  schema(input.schemaVersion);
  if (input.ownerConfirmation !== true) invalid();
  return fingerprint("DISCARD", { importId: id(input.importId), bookId: id(input.bookId),
    idempotencyKey: id(input.idempotencyKey), ownerConfirmation: true });
}

function fingerprint(operation: string, input: object): string {
  return stableFingerprint({ schema: "tradvera.statement-import.operation-input.v1", operation, input });
}

function decision(value: StatementImportReviewDecisionV2): StatementImportReviewDecisionV2 {
  const fieldId = id(value.fieldId);
  if (value.decision !== "CONFIRM" && value.decision !== "CORRECT" && value.decision !== "REJECT") invalid();
  const correctedValue = value.correctedValue === null ? null : text(value.correctedValue, 1, 2_000);
  if ((value.decision === "CORRECT") !== (correctedValue !== null)) invalid();
  return { fieldId, decision: value.decision, correctedValue };
}
function schema(value: string): void { if (value !== SCHEMA_VERSION) invalid(); }
function id(value: string): string {
  const result = text(value, 3, 160);
  if (!/^[A-Za-z0-9._:/-]+$/u.test(result)) invalid();
  return result;
}
function stable(value: string): string {
  const result = text(value, 74, 74);
  if (!/^sha256:v1:[a-f0-9]{64}$/u.test(result)) invalid();
  return result;
}
function text(value: string, minimum: number, maximum: number): string {
  if (typeof value !== "string" || value.trim() !== value || value.length < minimum || value.length > maximum
    || /[\u0000-\u001f\u007f]/u.test(value)) invalid();
  return value;
}
function uint(value: number): number {
  if (!Number.isSafeInteger(value) || value < 0) invalid();
  return value;
}
function invalid(): never { throw new Error("STATEMENT_IMPORT_OPERATION_INPUT_INVALID"); }
